/**
 * WriterApplicationCard.jsx — Profile-page card that lets a reader
 * apply to become a scribe, and tracks the application across all
 * states (eligible / pending / rejected / writer). Iter 70.
 *
 * States rendered:
 *   • eligible — "Become a Scribe" CTA → opens pitch textarea → submit
 *   • pending  — amber card "Application under review" + cancel option
 *   • rejected — rose card showing the rejection note + Resubmit
 *   • writer   — emerald confirmation (rare; usually the card hides for
 *                scribes because they already see writer-only UI)
 *
 * The card calls these endpoints:
 *   GET /api/me/writer-status
 *   POST /api/me/request-writer    { pitch }
 *   POST /api/me/cancel-writer-request
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  PenLine, Hourglass, CheckCircle2, XCircle, Loader2, Sparkles,
  ChevronRight, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const WriterApplicationCard = () => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [pitch, setPitch] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/me/writer-status`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setInfo(d);
    } catch (e) {
      setError(typeof e?.message === 'string' ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/me/request-writer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pitch }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Could not submit');
      toast.success('Application submitted — admin will review it soon.');
      setOpen(false);
      setPitch('');
      await refresh();
    } catch (e) {
      toast.error(typeof e?.message === 'string' ? e.message : 'Error');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm('Withdraw your writer application?')) return;
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/me/cancel-writer-request`, { method: 'POST' });
      if (!r.ok) throw new Error('Could not cancel');
      toast.success('Application withdrawn.');
      await refresh();
    } catch (e) {
      toast.error(typeof e?.message === 'string' ? e.message : 'Error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div
        className="rounded-2xl border border-white/10 bg-[#12131A] p-4 flex items-center gap-2 text-sm text-white/60"
        data-testid="writer-app-card-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  if (error) {
    return null; // silently hide on error — avoid noisy red box on Profile
  }
  if (!info) return null;
  // For admins/coadmins/already-approved scribes the card is hidden —
  // they don't need a CTA they can't act on.
  if (info.status === 'writer' || info.status === 'admin' || info.status === 'coadmin') {
    return null;
  }

  // ──────────────────────────────────────────────────────────────
  // ELIGIBLE — show the "Become a Scribe" CTA.
  // ──────────────────────────────────────────────────────────────
  if (info.status === 'eligible') {
    return (
      <section
        className="rounded-2xl border border-fuchsia-400/30 bg-[#12131A] p-5"
        data-testid="writer-app-card-eligible"
      >
        <header className="flex items-center gap-2 mb-2">
          <span className="w-8 h-8 rounded-lg bg-fuchsia-400/15 border border-fuchsia-400/30 grid place-items-center">
            <PenLine className="w-4 h-4 text-fuchsia-300" />
          </span>
          <div>
            <p className="eyebrow text-fuchsia-300">Scribe program</p>
            <p className="text-[11px] text-white/50">Publish text · audio · cinematic drops</p>
          </div>
        </header>
        <p className="font-display text-xl font-black text-white leading-snug mb-2">
          Become a Scribe<span className="text-fuchsia-300">.</span>
        </p>
        <p className="text-xs text-white/70 leading-relaxed mb-3">
          Earn 10 coins per 1,000 words published &amp; per 5 minutes of audio.
          Get your own profile, royalty tracking, and a Scribe Journey.
        </p>

        {!open ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-fuchsia-500 hover:bg-fuchsia-400 text-slate-900 text-sm font-extrabold"
            data-testid="writer-app-open-form"
          >
            <Sparkles className="w-4 h-4" /> Apply now
          </button>
        ) : (
          <div className="space-y-2" data-testid="writer-app-form">
            <p className="eyebrow text-white/45">Tell admin a bit about you (optional)</p>
            <textarea
              value={pitch}
              onChange={(e) => setPitch(e.target.value)}
              placeholder="What will you write? Share genre, sample link, or just a vibe."
              rows={4}
              maxLength={1000}
              className="w-full bg-[#0B0C10] border border-white/10 rounded-xl text-white text-sm p-3 placeholder:text-white/30 focus:outline-none focus:border-fuchsia-400/50 resize-y"
              data-testid="writer-app-pitch"
            />
            <div className="flex items-center justify-between text-[10px] text-white/40 tabular-nums">
              <span>{pitch.length}/1000</span>
              <span className="uppercase tracking-widest font-bold">Admin will be notified</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setOpen(false); setPitch(''); }}
                disabled={busy}
                className="px-3 py-2 rounded-xl border border-white/15 text-white/70 hover:text-white text-xs font-bold"
                data-testid="writer-app-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-fuchsia-500 hover:bg-fuchsia-400 disabled:bg-slate-700 text-slate-900 text-sm font-extrabold"
                data-testid="writer-app-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
                Submit application
              </button>
            </div>
          </div>
        )}
      </section>
    );
  }

  // ──────────────────────────────────────────────────────────────
  // PENDING — application under admin review.
  // ──────────────────────────────────────────────────────────────
  if (info.status === 'pending') {
    return (
      <section
        className="rounded-2xl border border-amber-400/40 bg-amber-500/5 p-5"
        data-testid="writer-app-card-pending"
      >
        <header className="flex items-center gap-2 mb-2">
          <span className="w-8 h-8 rounded-lg bg-amber-400/20 border border-amber-400/40 grid place-items-center">
            <Hourglass className="w-4 h-4 text-amber-300" />
          </span>
          <div>
            <p className="eyebrow text-amber-300">Scribe application</p>
            <p className="text-[11px] text-white/55">
              Submitted {formatDate(info.application_at)}
            </p>
          </div>
        </header>
        <p className="font-display text-lg font-black text-white leading-snug mb-1">
          Under admin review.
        </p>
        <p className="text-xs text-white/65 leading-relaxed">
          You&apos;ll get a notification once an admin decides. Your reader
          profile keeps working in the meantime.
        </p>
        {info.pitch && (
          <details className="mt-3 group">
            <summary className="text-[10px] text-amber-300 uppercase tracking-widest font-extrabold cursor-pointer">
              Your pitch
            </summary>
            <p className="mt-2 text-xs text-white/70 whitespace-pre-wrap bg-[#0B0C10] border border-white/5 rounded-lg p-2">
              {info.pitch}
            </p>
          </details>
        )}
        <button
          type="button"
          onClick={cancel}
          disabled={busy}
          className="mt-3 text-[10px] uppercase tracking-widest font-extrabold text-white/55 hover:text-white"
          data-testid="writer-app-withdraw"
        >
          {busy ? 'Working…' : 'Withdraw application'}
        </button>
      </section>
    );
  }

  // ──────────────────────────────────────────────────────────────
  // REJECTED — show reason + resubmit.
  // ──────────────────────────────────────────────────────────────
  if (info.status === 'rejected') {
    return (
      <section
        className="rounded-2xl border border-rose-400/40 bg-rose-500/5 p-5"
        data-testid="writer-app-card-rejected"
      >
        <header className="flex items-center gap-2 mb-2">
          <span className="w-8 h-8 rounded-lg bg-rose-400/20 border border-rose-400/40 grid place-items-center">
            <XCircle className="w-4 h-4 text-rose-300" />
          </span>
          <div>
            <p className="eyebrow text-rose-300">Application not approved</p>
            <p className="text-[11px] text-white/55">Decided {formatDate(info.decided_at)}</p>
          </div>
        </header>
        {info.decision_note && (
          <div className="mb-3 rounded-lg border border-rose-400/30 bg-[#0B0C10] p-3 text-xs text-rose-100/90 whitespace-pre-wrap inline-flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-rose-300 flex-shrink-0 mt-0.5" />
            <span>{info.decision_note}</span>
          </div>
        )}
        {!open ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-rose-500/20 hover:bg-rose-500/30 border border-rose-400/40 text-rose-100 text-sm font-extrabold"
            data-testid="writer-app-resubmit-open"
          >
            <PenLine className="w-4 h-4" /> Resubmit application
          </button>
        ) : (
          <div className="space-y-2">
            <textarea
              value={pitch}
              onChange={(e) => setPitch(e.target.value)}
              placeholder="Address what came up in the rejection note…"
              rows={4}
              maxLength={1000}
              className="w-full bg-[#0B0C10] border border-white/10 rounded-xl text-white text-sm p-3 placeholder:text-white/30 focus:outline-none focus:border-rose-400/50 resize-y"
              data-testid="writer-app-resubmit-pitch"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setOpen(false); setPitch(''); }}
                disabled={busy}
                className="px-3 py-2 rounded-xl border border-white/15 text-white/70 hover:text-white text-xs font-bold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-rose-500 hover:bg-rose-400 disabled:bg-slate-700 text-white text-sm font-extrabold"
                data-testid="writer-app-resubmit-send"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Send revised application
              </button>
            </div>
          </div>
        )}
      </section>
    );
  }

  return null;
};

export default WriterApplicationCard;
